/**
 * Module-level LRU absorbing the rapid `getBars` re-requests TV issues during
 * pan/zoom. Keyed by `${symbolId}|${resolution}|${from}|${to}` — the `|`
 * delimiter is illegal in every component (symbolIds use `:` and `/`,
 * resolutions are alphanumeric, from/to are unix seconds) so key collisions
 * are structurally impossible.
 *
 * The secondary index maps each symbolId → set of cache keys so a lifecycle
 * transition (perp delist, prediction resolution) can evict everything for
 * that symbol in one pass, then call `onResetCacheNeededCallback` on TV.
 * Without the eviction, the very next `getBars` would re-serve stale rows.
 */
import { notEmpty } from "@orb-labs/types";
const MAX_ENTRIES = 64;
const cache = new Map();
const bySymbol = new Map();
export function makeBarCacheKey(symbolId, resolution, from, to) {
    return `${symbolId}|${resolution}|${from}|${to}`;
}
export function getCachedBars(key) {
    const bars = cache.get(key);
    if (bars) {
        // LRU bump: re-insert at the tail.
        cache.delete(key);
        cache.set(key, bars);
    }
    return bars;
}
export function setCachedBars(symbolId, key, bars) {
    if (cache.has(key))
        cache.delete(key);
    cache.set(key, bars);
    let keys = bySymbol.get(symbolId);
    if (!keys) {
        keys = new Set();
        bySymbol.set(symbolId, keys);
    }
    keys.add(key);
    // Evict the oldest entry once we exceed the bound.
    if (cache.size > MAX_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (notEmpty(oldest)) {
            cache.delete(oldest);
            removeKeyFromIndex(oldest);
        }
    }
}
export function evictSymbol(symbolId) {
    const keys = bySymbol.get(symbolId);
    if (!keys)
        return;
    for (const key of keys)
        cache.delete(key);
    bySymbol.delete(symbolId);
}
export function __resetBarCacheForTests() {
    cache.clear();
    bySymbol.clear();
}
function removeKeyFromIndex(key) {
    const sep = key.indexOf("|");
    if (sep < 0)
        return;
    const symbolId = key.slice(0, sep);
    const keys = bySymbol.get(symbolId);
    if (!keys)
        return;
    keys.delete(key);
    if (keys.size === 0)
        bySymbol.delete(symbolId);
}
